import { type Extensions } from "@tiptap/core"
import Document from "@tiptap/extension-document"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import TextAlign from "@tiptap/extension-text-align"
import Typography from "@tiptap/extension-typography"
import Underline from "@tiptap/extension-underline"
import StarterKit from "@tiptap/starter-kit"
import cn from "classnames"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import ExampleList from "./example_list"
import { type BlockProps } from "."
import { graphql, getFragment } from "@/api"
import StaticTiptapDisplay from "@/client/components/static_tiptap_display"
import { ArrowRight, ChevronRightLarge } from "@/skylticons"

const HeadingDocument = Document.extend({ content: "heading" })
const ParagraphDocument = Document.extend({ content: "paragraph+" })

export const heroHeadingStack: Extensions = [
  StarterKit.configure({
    document: false,
    bold: false,
    italic: false,
    heading: {
      levels: [2],
      HTMLAttributes: {
        class: "mb-3",
      },
    },
  }),
  HeadingDocument,
  Placeholder,
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["heading"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const heroParagraphStack: Extensions = [
  StarterKit.configure({
    document: false,
    paragraph: {
      HTMLAttributes: {
        class: "mb-4 paragraph-sm",
      },
    },
  }),
  ParagraphDocument,
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["paragraph"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const HeroFragment = graphql(`
  fragment FrontpagesHero on FrontpagesHero {
    id
    buttonLabel
    buttonUrl
    headingJSON: heading
    description

    desktopImage: desktop {
      data
      width
      height
      pngDesktop
      pngDesktop2x
      pngTablet
      pngTablet2x
      webpDesktop
      webpDesktop2x
      webpTablet
      webpTablet2x
    }

    mobileImage: mobile {
      data
      width
      height
      pngMobile
      pngMobile2x
      webpMobile
      webpMobile2x
    }

    examples {
      id

      example {
        id

        ...FrontpageExampleListExample_Example
      }
    }
  }
`)

const HeroBlock: React.FC<BlockProps<typeof HeroFragment>> = ({ blockable, businessRole }) => {
  const block = getFragment(HeroFragment, blockable)
  const examples = block.examples

  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const close = () => {
    setOpen(false)
  }

  useEffect(() => {
    if (!open || !containerRef.current) return

    const listener = (e: MouseEvent) => {
      if (!containerRef.current) return

      if (e.target instanceof Element && !containerRef.current.contains(e.target)) {
        close()
      }
    }

    document.addEventListener("click", listener)

    return () => document.removeEventListener("click", listener)
  }, [open])

  return (
    <div>
      <div className="container pt-0">
        <div
          ref={containerRef}
          className="relative h-auto overflow-hidden rounded-t-2xl border border-white-10 sm:h-[404px] lg:h-[376px] lg:rounded-2xl"
        >
          <div className="absolute inset-0 z-0 lg:pr-[320px] xl:pr-[520px]">
            <picture>
              <source
                srcSet={`${block.desktopImage.webpDesktop}, ${block.desktopImage.webpDesktop2x} 2x`}
                media="(min-width: 1280px)"
                type="image/webp"
              />
              <source
                srcSet={`${block.desktopImage.pngDesktop}, ${block.desktopImage.pngDesktop2x} 2x`}
                media="(min-width: 1280px)"
              />
              <source
                srcSet={`${block.desktopImage.webpTablet}, ${block.desktopImage.webpTablet2x} 2x`}
                media="(min-width: 640px)"
                type="image/webp"
              />
              <source
                srcSet={`${block.desktopImage.pngTablet}, ${block.desktopImage.pngTablet2x} 2x`}
                media="(min-width: 640px)"
              />
              <source
                srcSet={`${block.mobileImage.webpMobile}, ${block.mobileImage.webpMobile2x} 2x`}
                type="image/webp"
              />
              <img
                srcSet={`${block.mobileImage.pngMobile}, ${block.mobileImage.pngMobile2x} 2x`}
                width={block.mobileImage.width}
                height={block.mobileImage.height}
                className="h-full w-full object-cover object-bottom sm:object-left"
                loading="lazy"
              />
            </picture>
          </div>
          <div
            className={cn(
              "absolute inset-y-0 right-0 z-10 hidden rounded-r-2xl border-l border-white-10 bg-white py-8 transition-all duration-300 lg:block",
              {
                "w-[320px] xl:w-[520px]": !open,
                "w-[calc(100%-144px)] rounded-l-2xl": open,
              }
            )}
          >
            <div className="relative">
              <ExampleList
                noHover
                hideButtons={!open}
                className="px-8"
                examples={examples.map(e => e.example)}
                businessRole={businessRole}
                gtmTag="fp-campaign-item"
                moreButtonGtmTag="fp-campaign-more"
              />

              <button
                className={cn(
                  "button button-secondary button-medium icon-only raised absolute right-2 top-24 -translate-y-5",
                  { hidden: open }
                )}
                type="button"
                onClick={() => setOpen(true)}
                data-gtm="fp-campaign-more"
              >
                <ChevronRightLarge className="h-10 w-10" />
              </button>
            </div>
            {open && (
              <button
                className="button button-secondary button-medium icon-right raised absolute left-4 top-4 -translate-x-full"
                type="button"
                onClick={close}
              >
                {t("misc.close")} <ArrowRight className="h-10 w-10" />
              </button>
            )}
          </div>

          <div className="relative z-0 flex items-center px-4 py-8 sm:h-full sm:w-[432px] sm:px-10">
            <div>
              <StaticTiptapDisplay extensions={heroHeadingStack} json={block.headingJSON} />
              <StaticTiptapDisplay extensions={heroParagraphStack} json={block.description} />

              <a
                href={block.buttonUrl}
                className="button button-emphasis button-large mb-48 sm:mb-0"
                data-gtm="fp-campaign-button"
              >
                {block.buttonLabel}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 lg:hidden">
        <ExampleList
          noHover
          examples={examples.map(e => e.example)}
          businessRole={businessRole}
          gtmTag="fp-campaign-item"
          moreButtonGtmTag="fp-campaign-more"
        />
      </div>
    </div>
  )
}

export default HeroBlock
